import { useEffect, useRef, useState, useCallback } from "react";
import { Bell, Mail, Check, X, CalendarClock, Zap, Landmark, Sun } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const ICON = { upcoming: CalendarClock, surprise: Zap, cot: Landmark };
const COLOR = { high: "text-red-400", medium: "text-amber-400", low: "text-slate-400" };

export function NotificationBell() {
  const { t } = useLang();
  const timeAgo = (iso) => {
    if (!iso) return "";
    const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 3600) return t("nb.agoMin", { n: Math.floor(s / 60) });
    if (s < 86400) return t("nb.agoH", { n: Math.floor(s / 3600) });
    return t("nb.agoD", { n: Math.floor(s / 86400) });
  };
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [unread, setUnread] = useState(0);
  const [emailOn, setEmailOn] = useState(false);
  const [briefOn, setBriefOn] = useState(false);
  const ref = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/alerts?limit=30");
      setAlerts(data.alerts || []);
      setUnread(data.unread || 0);
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    load();
    api.get("/alerts/settings").then(({ data }) => { setEmailOn(data.alert_email_enabled); setBriefOn(data.golden_brief_enabled); }).catch(() => {});
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const toggleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      try { await api.post("/alerts/seen"); setUnread(0); } catch { /* noop */ }
    }
  };

  const toggleEmail = async () => {
    const v = !emailOn;
    setEmailOn(v);
    try { await api.put("/alerts/settings", { alert_email_enabled: v }); } catch { setEmailOn(!v); }
  };

  const toggleBrief = async () => {
    const v = !briefOn;
    setBriefOn(v);
    try { await api.put("/alerts/settings", { golden_brief_enabled: v }); } catch { setBriefOn(!v); }
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggleOpen} data-testid="notif-bell"
        className="relative flex h-9 w-9 items-center justify-center rounded-lg border border-slate-800 bg-[#11141A] text-slate-400 transition-colors hover:text-white">
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span data-testid="notif-badge" className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notif-panel" className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-xl border border-slate-800 bg-[#0E1118] shadow-2xl">
          <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
            <span className="text-sm font-semibold text-white">{t("nb.alerts")}</span>
            <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-white"><X className="h-4 w-4" /></button>
          </div>
          <button onClick={toggleEmail} data-testid="notif-email-toggle"
            className="flex w-full items-center justify-between border-b border-slate-800 px-4 py-2.5 text-xs text-slate-400 hover:bg-slate-800/40">
            <span className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> {t("nb.emailAlerts")}</span>
            <span className={`flex h-4 w-8 items-center rounded-full px-0.5 transition-colors ${emailOn ? "justify-end bg-emerald-500" : "justify-start bg-slate-700"}`}>
              <span className="h-3 w-3 rounded-full bg-white" />
            </span>
          </button>
          <button onClick={toggleBrief} data-testid="notif-brief-toggle"
            className="flex w-full items-center justify-between border-b border-slate-800 px-4 py-2.5 text-xs text-slate-400 hover:bg-slate-800/40">
            <span className="flex items-center gap-2"><Sun className="h-3.5 w-3.5 text-amber-400" /> {t("nb.goldenBrief")} <span className="text-[10px] text-slate-600">{t("nb.at8")}</span></span>
            <span className={`flex h-4 w-8 items-center rounded-full px-0.5 transition-colors ${briefOn ? "justify-end bg-amber-500" : "justify-start bg-slate-700"}`}>
              <span className="h-3 w-3 rounded-full bg-white" />
            </span>
          </button>
          <div className="max-h-96 overflow-y-auto">
            {alerts.length === 0 ? (
              <p className="px-4 py-8 text-center text-xs text-slate-500">{t("nb.none")}</p>
            ) : alerts.map((a) => {
              const Icon = ICON[a.type] || Bell;
              return (
                <div key={a.key} className="flex gap-2.5 border-b border-slate-800/60 px-4 py-3 last:border-0" data-testid="notif-item">
                  <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${COLOR[a.severity] || "text-slate-400"}`} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-200">{a.title}</div>
                    <div className="text-xs text-slate-500">{a.body}</div>
                    <div className="mt-0.5 text-[10px] text-slate-600">{timeAgo(a.created_at)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
